import { useState, useEffect } from 'react';
import { locationContentGenerator, LocationContent } from '@/utils/locationContent';

// Simple in-memory cache to avoid regenerating content on every page load
const contentCache = new Map<string, LocationContent>();
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour
const cacheTimestamps = new Map<string, number>();

export const useLocationContent = (cityName: string) => {
  const [content, setContent] = useState<LocationContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check cache first
        const cached = contentCache.get(cityName);
        const cacheTime = cacheTimestamps.get(cityName);
        const now = Date.now();

        if (cached && cacheTime && (now - cacheTime) < CACHE_DURATION) {
          setContent(cached);
          setLoading(false);
          return;
        }

        // Generate fresh content
        const generatedContent = await locationContentGenerator.generateAllContent(cityName);
        
        // Cache the result
        contentCache.set(cityName, generatedContent);
        cacheTimestamps.set(cityName, now);
        
        setContent(generatedContent);
      } catch (err) {
        console.error('Error loading location content:', err);
        setError('Failed to load location-specific content');
        
        // Set fallback content
        const fallbackContent: LocationContent = {
          roofingChallenges: `${cityName} requires specialized roofing expertise and quality materials for long-lasting protection.`,
          buildingCodes: `All roofing projects in ${cityName} comply with North Carolina Building Code requirements and local permit processes.`,
          weatherConsiderations: `Weather-appropriate roofing solutions designed specifically for ${cityName}'s climate conditions.`,
          localServices: `Professional roofing services throughout ${cityName} and surrounding areas with local expertise.`,
          communityInfo: `Carolina Trophy Roofs is proud to serve the ${cityName} community with quality roofing solutions.`,
          testimonials: `"Excellent service and quality workmanship. Highly recommend Carolina Trophy Roofs!" - Local ${cityName} Customer\n\n"Professional team, fair pricing, and beautiful results." - ${cityName} Homeowner`,
          pricingFactors: `Competitive pricing for all ${cityName} roofing projects with flexible financing options and transparent estimates.`
        };
        
        setContent(fallbackContent);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [cityName]);

  // Clear old cache entries periodically
  useEffect(() => {
    const cleanup = () => {
      const now = Date.now();
      for (const [key, timestamp] of cacheTimestamps.entries()) {
        if (now - timestamp > CACHE_DURATION) {
          contentCache.delete(key);
          cacheTimestamps.delete(key);
        }
      }
    };

    const interval = setInterval(cleanup, CACHE_DURATION);
    return () => clearInterval(interval);
  }, []);

  return { content, loading, error };
};